import { BrokerDisposableHandler, PrimariaApi, PrimariaRegionHost } from "@uxland/primary-shell";
import { parse } from "date-fns";
import { LitElement, css, html, unsafeCSS } from "lit";
import { customElement, property } from "lit/decorators.js";
import { lazyInject } from "../../../../infrastructure/ioc/container";
import { TYPES } from "../../../../infrastructure/ioc/types";
import { connectedProperty } from "../../../../infrastructure/state/connected-property";
import { isAnyCollectionBusySelector, isSomeCollectionLoadedAndOtherLoading } from "../../../add/handle-busy/selectors";
import { IActivityHistoryGroup } from "../../../domain/model";
import { HandleRefreshHistoryItems } from "../../../refresh/request";
import { SearchActivityHistoryItems } from "../../../search/request";
import { activityHistoryGroupedItems } from "../../../selectors";
import { ActivityHistoryTimeline } from "../timeline/activity-history-timeline";
import styles from "./styles.css?inline";
import { template } from "./template";

//@ts-ignore
@customElement("activity-history-main")
export class ActivityHistoryMain extends PrimariaRegionHost(LitElement) {
  render() {
    return html`${template(this)}`;
  }

  static styles = css`
    ${unsafeCSS(styles)}
  `;

  subscriptions: BrokerDisposableHandler[] = [];
  private _searchTimeout: number | null = null;

  connectedCallback() {
    super.connectedCallback();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._unsubscribeEvents();
  }

  firstUpdated() {
    super.firstUpdated();
    this._subscribeEvents();
  }

  @property({ type: Boolean, reflect: true })
  maximized = false;

  @lazyInject(TYPES.primaryApi)
  api: PrimariaApi;

  @connectedProperty(activityHistoryGroupedItems)
  historyGroups: IActivityHistoryGroup[];

  @connectedProperty(isSomeCollectionLoadedAndOtherLoading)
  isSomeCollectionLoadedAndOtherLoading: boolean;

  @connectedProperty(isAnyCollectionBusySelector)
  isAnyCollectionBusy: boolean;

  // Administrative users cannot trigger the cronogram action, so the actions
  // that expose it (the 3-dots menu and the maximized cronogram button) are
  // hidden for them.
  get isAdministrative(): boolean {
    return !!this.api?.userManager?.isUserRoleAdministrative();
  }

  _maximize() {
    this.maximized = true;
  }

  _minimize() {
    this.maximized = false;
  }

  _subscribeEvents() {
    const mainViewSubscription = this.api.broker.subscribe("mainViewChanged", ({ viewId }) => {
      if (viewId === "clinical-monitoring" || viewId === "admin-clinical-monitoring") {
        this.maximized = false;
      }
    });
    this.subscriptions.push(mainViewSubscription);
  }

  _unsubscribeEvents() {
    this.subscriptions.forEach((s) => s.dispose());
  }

  @property()
  searchQuery = "";

  _handleSelectDate(date: string) {
    const selectedDate = parse(date, "dd/MM/yyyy", new Date());
    this.scrollToClosestDate(selectedDate);
  }

  _handleSearchChange(event: CustomEvent) {
    if (typeof event.detail !== "string") return;
    this.searchQuery = event.detail;

    if (this._searchTimeout) {
      clearTimeout(this._searchTimeout);
    }

    this._searchTimeout = setTimeout(() => {
      this.api.broker.send(new SearchActivityHistoryItems(this.searchQuery));
    }, 300);
  }

  async _raiseEcapCronogramEvent() {
    const CIP = (await this.api.broker.send("get_patient_cip")) as string;
    this.api.ecapEventManager.publish("CRONOGRAMA", "NO_TANCAR", { CIP });
  }

  scrollToClosestDate(date: Date) {
    const selectedDate = date.getTime();
    const timeline = this.shadowRoot.querySelector("activity-history-timeline") as ActivityHistoryTimeline;
    timeline._scrollIntoDate(selectedDate);
  }

  _refreshHistory() {
    this.api.broker.send(new HandleRefreshHistoryItems());
  }
}
